import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import BrandTextLink from "../../components/BrandTextLink/BrandTextLink";
import { fileIcon as FileIcon, linkIcon as LinkIcon } from "../../assets/icons";
import {
  CourseCoverVisual,
  CourseHeroIdentity,
  CourseHeroText,
} from "../../components/CourseCover/CourseCover";
import { useNotify } from "../../context/NotificationContext";
import {
  fetchStudentCourses,
  fetchStudentDocument,
  fetchStudentLessons,
  fetchStudentCourseOrders,
} from "../../services/studentPortalService/studentPortalService";
import type {
  StudentCourse,
  StudentDocumentStatus,
  StudentLesson,
  StudentCourseOrder,
} from "../../services/studentPortalService/studentPortalService.types";
import { logger } from "../../utils/logger";
import StudentBreadcrumbs from "./StudentBreadcrumbs";
import {
  CourseLessonPageShell,
  CourseSummary,
  DemoEmptyState,
  DemoStatusChip,
  EntityDescription,
  EntityMeta,
  EntityTitle,
  LessonChevron,
  LessonContentSection,
  LessonMain,
  LessonMark,
  LessonMarkIcon,
  LessonMarks,
  LessonOrder,
  LessonRow,
  LessonsList,
  LessonTitle,
  SectionHeading,
  SummaryBlock,
  SummaryDivider,
  SummaryLabel,
  SummaryProgress,
  SummaryTrack,
  SummaryValue,
  type StudentStatusTone,
} from "./StudentCourseLesson.styles";
import { CatalogGroup, CatalogGroupTitle } from "./studentCourses.styles";
import { EmptyState, LoadingState } from "./studentPortal.styles";

const documentTone = (status: string): StudentStatusTone => {
  if (status === "issued") return "success";
  if (status === "planned") return "info";
  if (status === "not_issued") return "warning";
  return "neutral";
};

const groupLessonsByCategory = (lessons: StudentLesson[]) =>
  lessons.reduce<Array<{ title: string; lessons: StudentLesson[] }>>((groups, lesson) => {
    const title = lesson.category_title?.trim() || "";
    const current = groups[groups.length - 1];
    if (!current || current.title !== title) {
      groups.push({ title, lessons: [lesson] });
    } else {
      current.lessons.push(lesson);
    }
    return groups;
  }, []);

const StudentCoursePage: React.FC = () => {
  const { courseId } = useParams();
  const courseIdNumber = Number(courseId);
  const [course, setCourse] = useState<StudentCourse | null>(null);
  const [lessons, setLessons] = useState<StudentLesson[]>([]);
  const [documentStatus, setDocumentStatus] = useState<StudentDocumentStatus | null>(null);
  const [orders, setOrders] = useState<StudentCourseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useNotify();

  useEffect(() => {
    if (!courseIdNumber) return;
    let active = true;
    Promise.all([
      fetchStudentCourses(),
      fetchStudentLessons(courseIdNumber),
      fetchStudentDocument(courseIdNumber),
      fetchStudentCourseOrders(courseIdNumber),
    ])
      .then(([courses, lessonItems, document, orderItems]) => {
        if (!active) return;
        setCourse(courses.find((item) => item.id === courseIdNumber) || null);
        setLessons(lessonItems);
        setDocumentStatus(document);
        setOrders(orderItems || []);
      })
      .catch((error) => {
        logger.error("Ошибка загрузки курса ученика:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить курс", life: 3000 });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [courseIdNumber, toast]);

  if (loading) return <LoadingState>Загружаем курс…</LoadingState>;
  if (!course) return <EmptyState>Курс недоступен или доступ к нему был отключен.</EmptyState>;

  const lessonGroups = groupLessonsByCategory(lessons);

  return (
    <CourseLessonPageShell>
      <StudentBreadcrumbs
        items={[
          { label: "Мои курсы", to: "/student/courses" },
          { label: course.title },
        ]}
      />

      <CourseHeroIdentity>
        <CourseCoverVisual src={course.cover} alt="" variant="hero" />
        <CourseHeroText>
          <EntityTitle>{course.title}</EntityTitle>
          <EntityMeta>{course.teacher.organization_name} · {course.category}</EntityMeta>
        </CourseHeroText>
      </CourseHeroIdentity>
      {course.description ? (
        <>
          <SectionHeading>Описание курса</SectionHeading>
          <LessonContentSection>
            <EntityDescription>{course.description}</EntityDescription>
          </LessonContentSection>
        </>
      ) : null}

      <CourseSummary>
        <SummaryBlock $grow>
          <SummaryLabel>Домашние задания</SummaryLabel>
          {course.progress.percent === null ? (
            <SummaryValue>В курсе их пока нет</SummaryValue>
          ) : (
            <>
              <SummaryTrack>
                <SummaryProgress $percent={course.progress.percent} />
              </SummaryTrack>
              <SummaryValue>
                Проверено <strong>{course.progress.rated} из {course.progress.total}</strong> · {course.progress.percent}%
              </SummaryValue>
            </>
          )}
        </SummaryBlock>

        <SummaryDivider aria-hidden="true" />

        <SummaryBlock>
          <SummaryLabel>Документ по курсу</SummaryLabel>
          {documentStatus?.document_type ? (
            <>
              <DemoStatusChip $tone={documentTone(documentStatus.status)}>
                {documentStatus.document_type_label}: {documentStatus.status_label}
              </DemoStatusChip>
              {documentStatus.comment ? <SummaryValue>{documentStatus.comment}</SummaryValue> : null}
            </>
          ) : (
            <SummaryValue>Преподаватель пока не указал статус</SummaryValue>
          )}
        </SummaryBlock>

        <SummaryDivider aria-hidden="true" />

        <SummaryBlock>
          <SummaryLabel>Приказы</SummaryLabel>
          {orders.length === 0 ? (
            <SummaryValue>Документов пока нет</SummaryValue>
          ) : (
            orders.map((order) => (
              <SummaryValue key={order.id}>
                <BrandTextLink href={order.download_url} target="_blank" rel="noreferrer">
                  {order.title}
                </BrandTextLink>
              </SummaryValue>
            ))
          )}
        </SummaryBlock>
      </CourseSummary>

      <SectionHeading>Уроки</SectionHeading>
      {lessons.length === 0 ? (
        <DemoEmptyState>В курсе пока нет уроков.</DemoEmptyState>
      ) : (
        lessonGroups.map((group, groupIndex) => (
          <CatalogGroup key={`${group.title || "no-category"}-${groupIndex}`}>
            {group.title ? <CatalogGroupTitle>{group.title}</CatalogGroupTitle> : null}
            <LessonsList>
              {group.lessons.map((lesson) => {
                const hasVideo = lesson.has_video || lesson.files_from_storage.some((item) => item.type === "video");
                const hasMaterials = lesson.files_from_storage.some((item) => item.type === "document");
                const hasMarks = Boolean(hasVideo || hasMaterials || lesson.with_link || lesson.with_assignments);

                return (
                  <LessonRow
                    key={lesson.id}
                    to={`/student/courses/${course.id}/lessons/${lesson.id}`}
                    state={{ courseTitle: course.title, lessonCount: lessons.length }}
                    $locked={Boolean(lesson.is_locked)}
                    aria-disabled={lesson.is_locked ? true : undefined}
                    title={lesson.is_locked ? lesson.lock_reason : undefined}
                    onClick={(event) => { if (lesson.is_locked) event.preventDefault(); }}
                  >
                    <LessonOrder>{lesson.order}</LessonOrder>
                    <LessonMain>
                      <LessonTitle>{lesson.is_locked ? `🔒 ${lesson.title}` : lesson.title}</LessonTitle>
                      {lesson.is_locked && lesson.lock_reason ? (
                        <LessonMarks><span>{lesson.lock_reason}</span></LessonMarks>
                      ) : hasMarks ? (
                        <LessonMarks>
                          {hasVideo ? (
                            <LessonMark>
                              <LessonMarkIcon><FileIcon aria-hidden="true" /></LessonMarkIcon>
                              видео
                            </LessonMark>
                          ) : null}
                          {hasMaterials ? (
                            <LessonMark>
                              <LessonMarkIcon><FileIcon aria-hidden="true" /></LessonMarkIcon>
                              материалы
                            </LessonMark>
                          ) : null}
                          {lesson.with_link ? (
                            <LessonMark>
                              <LessonMarkIcon><LinkIcon aria-hidden="true" /></LessonMarkIcon>
                              ссылка
                            </LessonMark>
                          ) : null}
                          {lesson.with_assignments ? <span>домашнее задание</span> : null}
                        </LessonMarks>
                      ) : null}
                    </LessonMain>
                    <LessonChevron aria-hidden="true" />
                  </LessonRow>
                );
              })}
            </LessonsList>
          </CatalogGroup>
        ))
      )}
    </CourseLessonPageShell>
  );
};

export default StudentCoursePage;
